/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GetNodeGroupByIdResponse } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useAddNodesToGroupMutation } from '@/hooks/mutation/useAddNodesToGroupMutation';
import { useAssignConfigToGroupMutation } from '@/hooks/mutation/useAssignConfigToGroupMutation';
import { useDeleteNodeGroupMutation } from '@/hooks/mutation/useDeleteNodeGroupMutation';
import { useRemoveNodesFromGroupMutation } from '@/hooks/mutation/useRemoveNodesFromGroupMutation';
import { useUpdateNodeGroupMutation } from '@/hooks/mutation/useUpdateNodeGroupMutation';
import { useNodeConfigByIdQuery } from '@/hooks/query/useNodeConfigByIdQuery';
import { useNodeGroupByIdQuery } from '@/hooks/query/useNodeGroupByIdQuery';
import { usePaginatedConfigRevisionsQuery } from '@/hooks/query/usePaginatedConfigRevisionsQuery';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { AddNodesToGroupDrawer } from '@/pages/FleetManagement/AddNodesToGroupDrawer';
import { UpdateFleetConfigDrawer } from '@/pages/FleetManagement/UpdateFleetConfigDrawer/UpdateFleetConfigDrawer';
import { ToggleMaintenanceModeModal } from '@/pages/Nodes/ToggleMaintenanceModeModal';
import { ToggleStateModal } from '@/pages/Nodes/ToggleStateModal';
import { Flex } from '@/primitives/Flex';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { DeleteFleetModal } from './DeleteFleetModal';
import { NodeGroupDetailsHeader } from './NodeGroupDetailsHeader';
import { EditFormFields, NodeGroupEditForm } from './NodeGroupEditForm';
import { NodeGroupNodesTable } from './NodeGroupNodesTable';

export type NodeGroupDetailsPageProps = {
  nodeGroupId: string;
};

export function NodeGroupDetailsPage({ nodeGroupId }: NodeGroupDetailsPageProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm<EditFormFields>();
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [isAddNodesDrawerOpen, setIsAddNodesDrawerOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isToggleModeModalOpen, setIsToggleModeModalOpen] = useState(false);
  const [isToggleStateModalOpen, setIsToggleStateModalOpen] = useState(false);
  const [isUpdateConfigDrawerOpen, setIsUpdateConfigDrawerOpen] = useState(false);
  const formName = Form.useWatch('name', form);
  const formDescription = Form.useWatch('description', form);
  const [message] = useMessage();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const navigate = useNavigate();
  const { t } = useMimicTranslation('fleets');

  const { mutate: updateNodeGroup, isPending: isUpdating } = useUpdateNodeGroupMutation({
    onSuccess: () => {
      message.success(t('feedback.fleetUpdatedSuccess'));
      setIsEditing(false);
      setSelectedNodeIds([]);
    },
    onError: () => {
      message.error(t('feedback.fleetUpdatedError'));
    },
  });

  const { mutate: addNodesToGroup, isPending: isAddingNodes } = useAddNodesToGroupMutation({
    onSuccess: () => {
      message.success(t('feedback.nodesAddedSuccess'));
      setIsAddNodesDrawerOpen(false);
    },
    onError: () => {
      message.error(t('feedback.nodesAddedError'));
    },
  });

  const { mutate: removeNodesFromGroup, isPending: isRemovingNodes } = useRemoveNodesFromGroupMutation({
    onSuccess: (variables) => {
      const count = variables.nodeIds.length;

      message.success(
        count > 1 ? t('feedback.nodesRemovedSuccessPlural', { count }) : t('feedback.nodesRemovedSuccess', { count }),
      );

      setSelectedNodeIds([]);
    },
    onError: () => {
      message.error(t('feedback.nodesRemovedError'));
    },
  });

  const { mutate: deleteNodeGroup, isPending: isDeleting } = useDeleteNodeGroupMutation({
    onSuccess: () => {
      message.success(t('feedback.fleetDeletedSuccess'));
      navigate(`/tenants/${tenantId}/fleet-management`);
    },
    onError: () => {
      message.error(t('feedback.fleetDeletedError'));
    },
  });

  const { mutate: assignConfigToGroup, isPending: isApplyingConfig } = useAssignConfigToGroupMutation({
    onSuccess: () => {
      message.success(t('feedback.configAppliedSuccess'));
    },
    onError: () => {
      message.error(t('feedback.configAppliedError'));
    },
  });

  const nodeGroupQuery = useNodeGroupByIdQuery({ nodeGroupId });
  const configId = nodeGroupQuery.data?.data.configId;
  const configRevisionId = nodeGroupQuery.data?.data.configRevisionId;
  const configQuery = useNodeConfigByIdQuery({ nodeConfigId: configId! });
  const revisionsQuery = usePaginatedConfigRevisionsQuery({
    params: { configId: configId || '' },
    type: 'inMemory',
  });
  const nodesQuery = usePaginatedNodesQuery({
    params: {
      filters: { nodeGroupId },
    },
  });

  const selectedRevision = useMemo(
    () => revisionsQuery.data?.data.find((rev) => rev.id === configRevisionId),
    [revisionsQuery.data?.data, configRevisionId],
  );

  const getNodeConfigurationPath = useCallback(
    (nodeConfigId: string) => `/tenants/${tenantId}/node-configs/${nodeConfigId}`,
    [tenantId],
  );

  const columnOptions = useMemo(
    () => ({
      expectedConfigId: configId ?? undefined,
      expectedRevisionId: configRevisionId ?? undefined,
    }),
    [configId, configRevisionId],
  );

  const handleSave = useCallback(() => {
    const nodeGroup = nodeGroupQuery.data?.data;
    if (!nodeGroup) return;

    const nameChanged = formName !== nodeGroup.name;
    const descriptionChanged = formDescription !== (nodeGroup.description || '');

    if (!nameChanged && !descriptionChanged) return;

    const data: { name?: string; description?: string } = {};
    if (nameChanged) data.name = formName;
    if (descriptionChanged) data.description = formDescription;

    updateNodeGroup({ nodeGroupId, data });
  }, [formName, formDescription, nodeGroupId, nodeGroupQuery.data?.data, updateNodeGroup]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setSelectedNodeIds([]);
  }, []);

  const handleRemoveNodes = useCallback(() => {
    removeNodesFromGroup({ nodeGroupId, nodeIds: selectedNodeIds });
  }, [nodeGroupId, selectedNodeIds, removeNodesFromGroup]);

  const handleApplyConfiguration = useCallback(() => {
    if (!configId || !selectedRevision?.revisionNumber) return;

    assignConfigToGroup({
      nodeGroupId,
      data: {
        configId,
        revisionNumber: selectedRevision.revisionNumber,
      },
    });
  }, [nodeGroupId, configId, selectedRevision?.revisionNumber, assignConfigToGroup]);

  return (
    <QueryWrapper<GetNodeGroupByIdResponse> queryResult={nodeGroupQuery}>
      {({ data: nodeGroup }) => (
        <QueryWrapper queryResult={nodesQuery}>
          {(nodesData) => (
            <>
              <ContentContainer width="100%">
                <NodeGroupDetailsHeader
                  nodeGroup={nodeGroup}
                  tenantId={tenantId!}
                  isEditing={isEditing}
                  isUpdating={isUpdating}
                  isDeleting={isDeleting}
                  formName={formName}
                  formDescription={formDescription}
                  configName={configQuery.data?.data?.name}
                  revisionNumber={selectedRevision?.revisionNumber}
                  onEdit={() => setIsEditing(true)}
                  onCancel={handleCancel}
                  onSave={handleSave}
                  onDelete={() => setIsDeleteModalOpen(true)}
                  onAddNodes={() => setIsAddNodesDrawerOpen(true)}
                  onToggleNodeMode={() => setIsToggleModeModalOpen(true)}
                  onToggleNodeState={() => setIsToggleStateModalOpen(true)}
                  onApplyConfiguration={handleApplyConfiguration}
                  onUpdateFleetConfig={() => setIsUpdateConfigDrawerOpen(true)}
                  isApplyingConfig={isApplyingConfig}
                />
                <Flex>
                  {isEditing && (
                    <NodeGroupEditForm
                      form={form}
                      initialName={nodeGroup.name}
                      initialDescription={nodeGroup.description || ''}
                    />
                  )}
                  <NodeGroupNodesTable
                    tenantId={tenantId!}
                    nodesData={nodesData}
                    onParamsChange={nodesQuery.updateQueryParams}
                    isEditing={isEditing}
                    selectedNodeIds={selectedNodeIds}
                    onSelectionChange={setSelectedNodeIds}
                    onRemoveNodes={handleRemoveNodes}
                    isRemovingNodes={isRemovingNodes}
                    columnOptions={columnOptions}
                    getNodeConfigurationPath={getNodeConfigurationPath}
                  />
                </Flex>
              </ContentContainer>
              <DeleteFleetModal
                open={isDeleteModalOpen}
                isDeleting={isDeleting}
                onConfirm={() => deleteNodeGroup({ nodeGroupId })}
                onCancel={() => setIsDeleteModalOpen(false)}
              />
              <AddNodesToGroupDrawer
                open={isAddNodesDrawerOpen}
                onClose={() => setIsAddNodesDrawerOpen(false)}
                onSubmit={(nodeIds) => addNodesToGroup({ nodeGroupId, nodeIds })}
                operatingSystem={nodeGroup.operatingSystem}
                isSubmitting={isAddingNodes}
              />
              <ToggleMaintenanceModeModal
                selectedNodes={nodesData.data}
                open={isToggleModeModalOpen}
                onClose={() => setIsToggleModeModalOpen(false)}
                onSuccess={() => setIsToggleModeModalOpen(false)}
              />
              <ToggleStateModal
                selectedNodes={nodesData.data}
                open={isToggleStateModalOpen}
                onClose={() => setIsToggleStateModalOpen(false)}
                onSuccess={() => setIsToggleStateModalOpen(false)}
              />
              <UpdateFleetConfigDrawer
                open={isUpdateConfigDrawerOpen}
                onClose={() => setIsUpdateConfigDrawerOpen(false)}
                nodeGroup={nodeGroup}
              />
            </>
          )}
        </QueryWrapper>
      )}
    </QueryWrapper>
  );
}
